import GlobalNotification from '../../component/common/snackbar';
import { ConditionType } from '../../component/form/search-key-input.component';
import { HeadCell } from '../../component/table/table.component';
import i18n from '../../i18n';
import { Condition, Reseller } from '../../models';
import apiManage from '../../request';
import _ from 'lodash';
import LSM from '../../modules/cache/tool-local-storage';
import { LOCAL_STORAGE_KEYS } from '../../modules/cache/local-storage.constant';
import { makeAutoObservable, observable } from 'mobx';
import { WillStopConfig } from '../../request/http';

// 经销商数据管理
export class ResellerState implements ResellerData {

    constructor() {
        makeAutoObservable(this);
    }

    // *********************
    // Attributes
    // *********************

    // 语言
    language: string = LSM.get(LOCAL_STORAGE_KEYS.LANGUAGE) || 'EN';
    // 经销商数据列表
    resellerList: Reseller[] = [];
    dataTotal: number = 0;
    // 搜索框内容
    searchValue: string = '';
    // 下拉框默认值 左边条件搜索下拉框
    selectValue: string = 'nickname';
    // 表格加载
    isTableLoading: boolean = false;
    // 新建经销商弹窗标识
    isOpenDialog: boolean = false;
    // 弹窗加载loading
    isLoading: boolean = false;
    // 排序参数 前端 ：0 不排序  1 降序 2 升序，后端：-1 降序 1 升序
    extraSortParams: {
        sort_key: string,
        sort_value: number
    } = { sort_key: '', sort_value: 0 };
    // 经销商额外传参
    extraParams: Reseller = {};

    // 搜索条件
    conditions = observable<ConditionType>([]);
    // 每页数量
    perPage = 9999;
    // 当前页码
    page = 0;
    // 显示经销商账号详情
    showUnit: boolean = false;
    // 点击经销商列表行详情按钮进去的数据
    enterResellerDetailObj: {
        key: number;
        resellerData: Reseller
    } = {
            // 进入经销商详情索引
            key: -1,
            // 进入经销商详情数据
            resellerData: {}
        };

    // 初始化
    async init() {
        // 初始化默认为倒序
        const params: any = { sort_value: '-1' };
        this.setSortParams(params);
        await this.getResellerListData();
    }

    setIsOpenDetail(val: boolean) {
        this.showUnit = val
    }

    // 获取表头排序状态值
    getSortStatus(key: string) {
        const listSort = this.extraSortParams;
        if (key === listSort.sort_key) {
            return listSort.sort_value
        }
        return 0;
    }

    // 表格排序
    async handleSort(headCell: HeadCell) {
        // 前端 ：0 不排序  1 降序 2 升序，后端：-1 降序 1 升序
        const sortValue = headCell?.sortStatus === 0 ? 1 : headCell?.sortStatus === 1 ? 2 : 0;
        // 设置传参
        this.setSortParams({
            sort_key: headCell.id,
            sort_value: sortValue,
        });
        this.getResellerListData();
    }

    /**
     * 设置排序参数
     * @param val 排序参数
     */
    setSortParams(val: typeof this.extraSortParams) {
        this.extraSortParams = val;
    }

    // 表格加载
    loadTable() {
        return this.isTableLoading = true;
    }

    // 加载完毕
    loadedTable() {
        return this.isTableLoading = false;
    }

    // 更新经销商数组
    updateData(data: Reseller[], totalCount: number) {
        this.resellerList = data || [];
        this.dataTotal = totalCount;
    }

    updateReseller(data: Reseller) {
        this.enterResellerDetailObj.resellerData = data;
    }

    // 打开新建经销商弹窗
    openAddResellerDialog() {
        this.isOpenDialog = true;
    }

    // 关闭新建经销商弹窗
    closeAddResellerDialog() {
        this.isOpenDialog = false;
    }

    // 返回经销商管理列表
    async goBack() {
        this.showUnit = false;
        // 并刷新数据
        await this.init();
    }

    // 开启加载  弹窗loading
    load() {
        this.isLoading = true
    }

    // 关闭加载  弹窗loading
    loaded() {
        this.isLoading = false;
    }

    /**
     * 删除经销商
     * @param deleteData 删除的数据
     * @param isBatch 是否为批量删除 不是则调用单独删除
     */
    async handleDelete(deleteData: Reseller[] | Reseller, isBatch: boolean): Promise<void> {
        /**
         * 1.删除经销商
         * 2.成功之后重新请求数据
         * @argument isBatch 是否为批量删除 不是则调用单独删除
         */
        try {
            // 根据参数选择传参
            const sendParam = isBatch ? (deleteData as Reseller[] ?? []).map(item => item._id) : (deleteData as Reseller ?? {})._id ?? '';
            // 请求数据  判断是否为批量删除  不是则调用单独删除
            const result = isBatch ? await apiManage.delete_reseller(sendParam as string[]) : await apiManage.delete_reseller_id_id(sendParam as string);
            // 请求成功
            if (!result?.error) {
                // 成功提示
                GlobalNotification.success(`${i18n.t(isBatch && (deleteData as Reseller[] ?? []).length > 1 ? 'general_message_text_batch_delete_successfully' : 'general_message_text_delete_successfully')}`);
                // 刷新数据
                await this.getResellerListData();
            } else {
                // 失败
                GlobalNotification.error(`${result?.status}: ${result?.result?.message} `);
            }
        } catch (error) {
            GlobalNotification.error(`${i18n.t('general_message_text_delete_failed')}`);
        }
    }


    /**
     * 获取经销商列表数据
     * @param extraParam 集团数据获取额外传参 有排序和搜索值
     */
    async getResellerListData(extraParam?: Reseller) {
        // 开启加载
        this.loadTable();
        // 排序参数转换
        const sortObj = this.extraSortParams.sort_key && this.extraSortParams.sort_value !== 0 ? {
            sort_key: this.extraSortParams.sort_key,
            // 前端 ：0 不排序  1 降序 2 升序，后端：-1 降序 1 升序
            sort_value: this.extraSortParams.sort_value === 2 ? 1 : -1,
        } : {
            // 默认排序:创建时间倒序
            sort_value: -1
        };

        // 发送的参数
        const params = {
            pagination_per_page: this.perPage,
            pagination_page: this.page + 1,
            key: LSM.get(LOCAL_STORAGE_KEYS.LANGUAGE) === 'CN' ? 'CN' : 'EN',
            ...this.extraParams,
            ...extraParam,
            ...sortObj,
        }
        // 请求集团接口
        const result = await apiManage.get_reseller_condition({ ...params }, {
            // api太慢。请求下一个时需要取消上个请求
            stop_by_unmount: true
        } as WillStopConfig);
        if (result.message === 'canceled') {
            // 请求被取消
            return
        }
        // 获取成功
        if (!result.error) {
            // 解构数据
            const { dbResults, totalCount } = result[0];
            this.updateData(dbResults || [], _.get(totalCount, '0.count', 0))
        } else {
            // 获取失败
            GlobalNotification.error(i18n.t('general_text_fetch_data_error'));
        }
        // 关闭加载
        this.loadedTable();
    }

    /**
     * 设置下拉框的值 左边搜索条件下拉框
     * @param value
     */
    async setSelectValue(value: string) {
        this.selectValue = value;
    }

    // 设置搜索条件
    async setConditions(val: ConditionType[]) {
        this.conditions.replace(val);
        const extraParams: { [key: string]: string[] } = {};
        val.forEach((item) => {
            if (item.key === 'phone_prefix_phone') {
                const phone = item.value.split(' ')[1]
                const phonePrefix = item.value.split(' ')[0]
                if (phone && phonePrefix) {
                    extraParams.phone = extraParams.phone ? _.concat(extraParams.phone, phone) : [phone];
                    extraParams.phone_prefix = extraParams.phone_prefix ? _.concat(extraParams.phone_prefix, phonePrefix) : [phonePrefix];
                } else {
                    extraParams.phone = extraParams.phone ? _.concat(extraParams.phone, phonePrefix) : [phonePrefix];
                }

            } else {
                extraParams[item.key] = extraParams[item.key] ? _.concat(extraParams[item.key], item.value) : [item.value];
            }

        })

        this.setExtraParams(extraParams);
        await this.getResellerListData(extraParams);

    }

    /**
     * 设置获取集团数据额外传参
     * @param extraParam
     */
    setExtraParams(extraParam: Reseller & Condition) {
        this.extraParams = extraParam;
    }


}


// 集团管理页数据
type ResellerData = {
    // 集团数据
    resellerList: Array<Reseller>;
    // 搜索框内容
    searchValue: string;
    // 下拉框默认值
    selectValue: string;
    // 表格加载
    isTableLoading: boolean;
    // 弹窗加载loading
    isLoading: boolean;
}
